import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import './WebDevPackage.css';
import { config } from '../config';

const PLANS = [
  {
    name: 'Starter',
    tagline: 'Perfect for small businesses & startups',
    price: '₹14,999',
    featured: false,
    features: [
      '5 Page Custom Website',
      'Responsive Design',
      'Basic SEO Setup',
      'Contact Form Integration',
      'Google Maps Integration',
      'Social Media Links',
      '1 Round of Revisions',
      '1 Month Free Support',
      'Hosting Setup Guidance',
      'Basic Analytics Setup',
    ],
    cta: 'Get Started',
    ctaClass: 'btn-ghost',
  },
  {
    name: 'Professional',
    tagline: 'The most popular choice for growing brands',
    price: '₹34,999',
    featured: true,
    features: [
      '15 Page Custom Website',
      'Premium UI/UX Design',
      'Advanced SEO Optimisation',
      'CMS Integration (WordPress)',
      'Blog Setup & Configuration',
      'Social Media Integration',
      'Speed & Performance Optimisation',
      'Google Analytics 4 Setup',
      '3 Rounds of Revisions',
      '3 Months Free Support',
      'WhatsApp Chat Integration',
      'Basic E-Commerce (up to 20 products)',
    ],
    cta: 'Get Started',
    ctaClass: 'btn-gold',
  },
  {
    name: 'Enterprise',
    tagline: 'Full-scale digital platform for serious brands',
    price: 'Custom',
    featured: false,
    features: [
      'Unlimited Pages',
      'Custom Web Application',
      'Full E-Commerce + Payment Gateway',
      'Custom Admin Dashboard',
      'Multi-Language Support',
      'Third-Party API Integrations',
      'Performance & Security Audit',
      'Unlimited Revisions',
      '6 Months Priority Support',
      'Dedicated Project Manager',
      'Source Code Delivery',
      'Annual Maintenance Plan Included',
    ],
    cta: 'Contact Us',
    ctaClass: 'btn-ghost',
  },
];

const COMPARE = [
  { feature:'Custom Design',       s:true,  p:true,  e:true  },
  { feature:'Mobile Responsive',   s:true,  p:true,  e:true  },
  { feature:'SEO Setup',           s:'Basic',p:'Advanced',e:'Full Audit' },
  { feature:'CMS Integration',     s:false, p:true,  e:true  },
  { feature:'E-Commerce',          s:false, p:'Basic',e:'Full' },
  { feature:'Custom Functionality',s:false, p:false, e:true  },
  { feature:'Support Duration',    s:'1 Mo',p:'3 Mo', e:'6 Mo' },
  { feature:'Source Code',         s:false, p:false, e:true  },
];

const FAQ = [
  { q:'How long does a project take?',       a:'Starter projects typically take 2–3 weeks. Professional projects take 4–6 weeks. Enterprise projects are scoped individually but usually 8–16 weeks.' },
  { q:'Do you offer payment in instalments?', a:'Yes! We typically work with a 50% upfront payment and 50% on final delivery. For larger projects we offer 3-part payment plans.' },
  { q:'Will my website be mobile-friendly?',  a:'Absolutely. All our websites are built mobile-first, ensuring a perfect experience across all devices and screen sizes.' },
  { q:'What happens after launch?',           a:'You get dedicated support for the duration included in your plan. We handle bug fixes, minor content updates, and technical issues at no extra cost.' },
];

function CheckIcon({ val, featured }){
  if(val === true)  return <span className="check-yes">✦</span>;
  if(val === false) return <span className="check-no">-</span>;
  return <span className={`check-val${featured?' check-val--feat':''}`}>{val}</span>;
}

export default function WebDevPackage(){
  useReveal();

  return(
    <div className="wdp-page">

      {/* PAGE HERO */}
      <div className="page-hero">
        <span className="ph-eye">Pricing Plans</span>
        <h1 className="ph-title">Web Development <span className="g">Packages</span></h1>
        <p className="ph-sub">Transparent pricing with zero hidden fees. Choose the plan that fits your goals - and scale anytime.</p>
      </div>

      {/* PRICING CARDS */}
      <section className="price-sect">
        <div className="container">
          <div className="price-grid">
            {PLANS.map((plan, i) => (
              <div
                key={plan.name}
                className={`price-card reveal d${i+1}${plan.featured?' feat':''}`}
              >
                {plan.featured && <div className="badge" style={{marginBottom:'1.5rem'}}>✦ Most Popular</div>}

                <div className="price-tier">{plan.name}</div>
                <div className="price-val">{plan.price}</div>
                {/* <div className="price-usd">{plan.usd} USD</div> */}
                <div className="price-period">{plan.period}</div>

                <p className="price-desc">{plan.tagline}</p>

                <ul className="price-feats">
                  {plan.features.map(f=>(
                    <li key={f}>
                      <span className="pf-check">✦</span>
                      {f}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={plan.ctaClass}
                  style={{marginTop:'auto'}}
                >
                  {plan.cta} →
                </Link>
              </div>
            ))}
          </div>

          <p className="price-note reveal">
            All packages include free domain guidance, SSL certificate setup, and a post-launch review call.
            Need something custom? <Link to={`${config.appUrl}contact`} style={{color:'var(--gold)'}}>Contact us →</Link>
          </p>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="compare-sect">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">Compare Plans</span>
            <h2 className="s-title">Feature <span className="g">Breakdown</span></h2>
          </div>

          <div className="compare-wrap reveal">
            <table className="compare-table">
              <thead>
                <tr>
                  <th className="compare-th compare-th--feat">Feature</th>
                  <th className="compare-th">Starter</th>
                  <th className="compare-th compare-th--pop">Professional</th>
                  <th className="compare-th">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map(row=>(
                  <tr key={row.feature} className="compare-row">
                    <td className="compare-td compare-td--label">{row.feature}</td>
                    <td className="compare-td"><CheckIcon val={row.s}/></td>
                    <td className="compare-td compare-td--pop"><CheckIcon val={row.p} featured/></td>
                    <td className="compare-td"><CheckIcon val={row.e}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-sect">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">Got Questions?</span>
            <h2 className="s-title">Frequently <span className="g">Asked</span></h2>
          </div>
          <div className="faq-grid">
            {FAQ.map((item, i)=>(
              <div key={i} className={`faq-card reveal d${(i%2)+1}`}>
                <div className="faq-q">
                  <span className="faq-icon">✦</span>
                  {item.q}
                </div>
                <p className="faq-a">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <div className="cta-sect">
        <div className="cta-glow"/>
        <div className="container" style={{position:'relative',textAlign:'center'}}>
          <div className="reveal">
            <h2 className="cta-title">Ready to Build Your <span className="g">Dream Website?</span></h2>
            <p className="cta-body">Start with a free consultation and let's map out the perfect plan for your business.</p>
            <div style={{display:'flex',gap:'1rem',justifyContent:'center',flexWrap:'wrap'}}>
              <Link to={`${config.appUrl}contact`} className="btn-primary">Start Your Project →</Link>
              <Link to={`${config.appUrl}maintenance-package`} className="btn-outline">View Maintenance Plans</Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}